/**
 * DiscountedProductsCarousel.jsx
 *
 * "Wide rectangle" card layout requested by the client:
 *   - Full-bleed image fills the entire card
 *   - Shop name + specialty + discount % overlaid on the image (with a
 *     dark gradient scrim for readability in both light and dark themes)
 *   - Location text shown BELOW the image
 *   - Auto-advances every 4.5s (paused on hover / drag / touch)
 *   - Manual navigation via arrow buttons and dots
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Tag, MapPin, Clock, ChevronLeft, ChevronRight, Store } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { shopName } from "@/i18n/dynamic";

function daysRemaining(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms) || ms <= 0) return null;
  return Math.ceil(ms / 86400000);
}

const AUTOPLAY_MS = 4500;

/**
 * Props:
 *   products : list of product objects
 *              { id, title, price, currency, discount_price, discount_percent,
 *                image, images, shop_id, shop_name, shop_category_slug, shop_address }
 *   categoryLabel  optional (slug → label) map for pretty category text
 */
export default function DiscountedProductsCarousel({ products = [], categoryLabelMap = {} }) {
  const { lang } = useLang();
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);

  const total = products.length;

  const scrollToIdx = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el || !el.firstChild) return;
    const cardW = el.firstChild.offsetWidth;
    const gap = 12;
    // In RTL layout, scrollLeft moves in the negative direction — but browsers
    // handle it inconsistently. Using scrollTo with `left: value * direction`
    // relative to the RTL start works because the browser normalizes it.
    el.scrollTo({ left: idx * (cardW + gap) * (document.documentElement.dir === "rtl" ? -1 : 1), behavior: "smooth" });
  }, []);

  /* ---------- Auto-advance every AUTOPLAY_MS ---------- */
  useEffect(() => {
    if (paused || dragging || total <= 1) return;
    const timer = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const nextIdx = (activeIdx + 1) % total;
      scrollToIdx(nextIdx);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [activeIdx, paused, dragging, total, scrollToIdx]);

  /* ---------- Track which card is centred (updates dots) ---------- */
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardW = el.firstChild?.offsetWidth || 1;
      const gap = 12;
      const idx = Math.round(el.scrollLeft / (cardW + gap));
      // Because layout is RTL, scrollLeft is negative on some browsers.
      // Use the absolute value; the card width heuristic still works.
      const absIdx = Math.min(total - 1, Math.max(0, Math.abs(idx)));
      setActiveIdx(absIdx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [total]);

  const next = () => scrollToIdx((activeIdx + 1) % total);
  const prev = () => scrollToIdx((activeIdx - 1 + total) % total);

  if (!total) return null;

  return (
    <div
      className="relative"
      data-testid="discounted-products-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setDragging(true)}
      onTouchEnd={() => setDragging(false)}
    >
      {/* Manual arrows (desktop only, small screens use swipe) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="السابق"
            data-testid="promo-arrow-prev"
            className="hidden md:flex absolute end-1 top-1/2 -translate-y-1/2 z-20 size-10 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center backdrop-blur-md border border-white/20 shadow-lg transition"
          >
            <ChevronRight className="size-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="التالي"
            data-testid="promo-arrow-next"
            className="hidden md:flex absolute start-1 top-1/2 -translate-y-1/2 z-20 size-10 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center backdrop-blur-md border border-white/20 shadow-lg transition"
          >
            <ChevronLeft className="size-5" />
          </button>
        </>
      )}

      {/* Cards row */}
      <div
        ref={scrollerRef}
        className="kh-scroll-x flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scroll-smooth"
      >
        {products.map((p) => {
          const img = p.image || (Array.isArray(p.images) && p.images[0]) || "";
          const percent = p.discount_percent || (
            p.price > 0 && p.discount_price
              ? Math.round(((p.price - p.discount_price) / p.price) * 100)
              : 0
          );
          const shopLabel = shopName({
            name: p.shop_name || "",
            name_ku: p.shop_name_ku || "",
            name_en: p.shop_name_en || "",
          }, lang);
          const daysLeft = daysRemaining(p.discount_expires_at);
          const specialty = categoryLabelMap[p.shop_category_slug] || p.shop_category_slug || "";
          return (
            <Link
              to={`/shop/${p.shop_id}`}
              key={p.id}
              data-testid={`discounted-product-${p.id}`}
              className="card-lift shrink-0 snap-start rounded-2xl overflow-hidden relative border border-border/60 hover:border-primary/50 bg-card transition group"
              style={{ width: "min(88vw, 340px)" }}
            >
              {/* Full-bleed image (wide rectangle 16:10) */}
              <div className="relative aspect-[16/10] w-full overflow-hidden">
                {img ? (
                  <img
                    src={img}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-black flex items-center justify-center">
                    <Store className="size-14 text-white/20" />
                  </div>
                )}

                {/* Bottom-up dark gradient scrim for text readability */}
                <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                {/* Discount % pill (top-start) */}
                {percent > 0 && (
                  <div className="absolute top-2.5 start-2.5 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600 text-white text-xs font-black shadow-lg">
                    <Tag className="size-3 fill-white" />
                    -{percent}%
                  </div>
                )}
                {/* Days-left pill (top-end) */}
                {daysLeft !== null && (
                  <div className="absolute top-2.5 end-2.5 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/95 text-white text-[11px] font-bold shadow-lg backdrop-blur-sm">
                    <Clock className="size-3" />
                    {daysLeft === 1 ? "آخر يوم" : `${daysLeft} يوم`}
                  </div>
                )}

                {/* Text overlaid on the image (bottom) */}
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  {shopLabel && (
                    <div className="text-[11px] font-bold text-white/90 uppercase tracking-wider line-clamp-1 mb-0.5">
                      {shopLabel}
                    </div>
                  )}
                  <h3 className="text-base font-black leading-tight line-clamp-1 drop-shadow">
                    {p.title}
                  </h3>
                  <div className="flex items-end gap-2 mt-1 flex-wrap">
                    <span className="text-rose-300 font-black text-lg drop-shadow">
                      {Number(p.discount_price || 0).toLocaleString("ar-IQ")}
                    </span>
                    <span className="text-white/60 text-xs line-through mb-1">
                      {Number(p.price || 0).toLocaleString("ar-IQ")}
                    </span>
                    <span className="text-white/60 text-[10px] mb-1">{p.currency || "د.ع"}</span>
                    {specialty && (
                      <span className="ms-auto text-[10px] font-bold bg-white/15 border border-white/25 backdrop-blur rounded-full px-2 py-0.5">
                        {specialty}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Location bar BELOW the image */}
              {p.shop_address && (
                <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground bg-muted/40 border-t">
                  <MapPin className="size-3.5 text-primary shrink-0" />
                  <span className="truncate">{p.shop_address}</span>
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Dot indicators */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {products.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToIdx(i)}
              aria-label={`الانتقال إلى الشريحة ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIdx ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
